import React, { useState, useContext, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pencil, XCircle, CheckCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import useListado from '../../hooks/useListado.js';
import DataTable from './DataTable.jsx';
import Pagination from './Pagination.jsx';
import FormModal from './FormModal.jsx';
import { exportTablePdf, exportTableExcel, buildTablePdf } from '../../utils/exportUtils.js';
import EmailModal from './EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * Página de listado + alta/edición/baja genérica, dirigida por config
 * (`config/resources.js`). Pensada para el tamaño real de Ibu (documentos simples o
 * con pocos efectos secundarios en el backend) — no incluye renglones/matriz/QR
 * (features de inventario de otros proyectos hermanos que no aplican aquí).
 *
 * @param {Object} cfg
 * @param {string} cfg.route        Ruta de la API (ej. 'clientes').
 * @param {string} cfg.title
 * @param {string} cfg.recurso      Raíz del permiso (ej. 'clientes').
 * @param {Array} cfg.columns       `{key,label,sortable,render(row)}`.
 * @param {Array} [cfg.fields]      Campos del FormModal (alta/edición). Si se omite,
 *                                   no hay botón "Nuevo" (solo lectura).
 * @param {Object} [cfg.extraParams] Params fijos agregados a cada consulta.
 * @param {(valores: Object, ctx: {editando: Object|null}) => Object} [cfg.transformBeforeSave]
 *        Transforma los valores del FormModal antes de POST/PATCH (ej. calcular
 *        campos derivados que el backend exige pero el form no captura). Agregado
 *        para Finanzas CxC (ver src/config/resources.finanzas-cxc.js) — opcional,
 *        no rompe páginas existentes que no lo usan.
 * @param {(row: Object, ctx: {recargar: Function, hasPermiso: Function}) => import('react').ReactNode} [cfg.extraRowActions]
 *        Botones de acción extra por fila (ej. "Pagar"), renderizados junto a
 *        Editar/Dar de baja. Agregado para Finanzas CxC — opcional. `hasPermiso` se
 *        pasa ya resuelto (obtenido una sola vez por render vía `useContext` aquí
 *        arriba) para que un `extraRowActions` que necesite gatear su propio botón
 *        nunca tenga que llamar un hook de contexto dentro del ciclo por fila (rompería
 *        las Reglas de los Hooks) — agregado para el botón "Permisos" de Usuarios.
 * @param {string} [cfg.editSuffix]
 *        Sufijo agregado a la URL de PATCH al editar (ej. '/importe' para que
 *        Cargos/CargosContenedor reajusten `Clientes`/`Contenedores.totalCargos` vía
 *        `editarImporte()` en vez del PATCH genérico `editar()`, que NO toca esos
 *        acumulados — ver auditoría de Contenedores 2026-09-28, bug real). Opcional,
 *        no afecta recursos que no lo declaren (mismo PATCH `/{route}/{id}` de
 *        siempre).
 * @param {boolean} [cfg.exportable]
 *        Muestra botones "PDF"/"Excel" junto al buscador, que exportan el listado
 *        FILTRADO completo (respeta búsqueda/orden, ignora la paginación de pantalla
 *        — mismo criterio que V1, que siempre exportaba con lo que tenía cargado).
 *        100% opt-in: recursos que no lo declaren no ven cambio alguno (ver
 *        auditoría 2026-09-28 -- gap real: NINGÚN listado de V2 tenía los botones
 *        PDF/Excel/eMail que V1 siempre trae). El botón "eMail" de V1 no se replica
 *        aquí -- requiere el backend de SendGrid, que todavía no existe (D-4.3).
 * @param {{titulo:string, subject:string, fileName:string, cuerpo:string}} [cfg.emailExport]
 *        Opt-in (requiere `exportable`): botón "eMail" entre PDF y Excel (como V1). Abre
 *        `EmailModal` (título `titulo`, email inicial vacío), arma el MISMO PDF que el botón
 *        PDF y lo manda con `enviarCorreoAdjunto` (asunto `subject`, adjunto `fileName`,
 *        cuerpo `cuerpo` dentro de `plantillaCorreoV1`).
 * @param {(row: Object) => boolean} [cfg.puedeEditarFila]
 *        Guard opcional por fila para el botón "Editar" (además del permiso global
 *        `puedeEditar`). Agregado en la segunda ronda de la auditoría CxC
 *        2026-09-28: V1 (`ListadoCargos.jsx`/`ListadoCargosContenedor.jsx`) oculta el
 *        botón de edición de un Cargo que ya tiene abonos aplicados (candado de
 *        cierre contable — evita editar el importe de algo que el cliente ya empezó
 *        a pagar). Si se omite, el botón se muestra siempre que `puedeEditar` sea
 *        true (comportamiento idéntico al que ya tenía `ResourcePage`) — 100%
 *        opt-in, no afecta ningún recurso que no lo declare.
 * @param {string[]} [cfg.ocultarIds]
 *        `_id` que V1 oculta de ESTE listado (solo visual; siguen existiendo y los selects
 *        de otras pantallas los ven). Se manda como `excluirIds` y el backend los excluye
 *        (paginación/total consistentes). Opt-in.
 * @param {Array} [cfg.filtrosExtra]
 *        Filtros opcionales de rango de fecha / catálogo, renderizados como controles
 *        arriba de la tabla (junto al buscador). Agregado en la segunda ronda de la
 *        auditoría CxC 2026-09-28: varios listados (Cargos/Abonos/Devoluciones/
 *        DevolucionesInversa, ver `resources.finanzas-cxc.jsx`) solo tenían buscador
 *        de texto libre, sin los filtros de "Fecha Inicio/Fin" y "Cliente" que V1
 *        siempre traía (`ListadoCargos.jsx` etc.). 100% opt-in -- ningún recurso que
 *        no lo declare ve cambio alguno. Cada entrada es:
 *          `{ key, label, type: 'date-range' | 'ref', refRoute, refLabel }`
 *        - `date-range`: dos inputs `type="date"` que llaman `cambiarFiltros` con
 *          `${key}Desde`/`${key}Hasta` (ej. `key:'fecha'` -> `fechaDesde`/
 *          `fechaHasta`, mismo nombre de query que ya soportan los servicios de
 *          Cargos/Abonos/Devoluciones/DevolucionesInversa en `IbuBack2`).
 *        - `ref`: un `<select>` que carga catálogo real de `refRoute` (igual patrón
 *          que `FormModal`) y filtra por `${key}` exacto (ej. `key:'clientes'`).
 *        Los valores viven en `useListado#filtros` (mismo estado que ya alimenta
 *        `listar`/exportación), así que búsqueda + orden + estos filtros + export
 *        PDF/Excel quedan consistentes entre sí sin lógica adicional.
 */
export default function ResourcePage({ cfg }) {
  const { hasPermiso } = useContext(AuthContext);
  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [exportando, setExportando] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);

  // `?tipo=` en la URL -- soporta los "disfraces" de Proveedores (Navieras/Agentes/
  // Forwarders: mismo modelo filtrado por `tipo`, ver Sidebar.jsx y DUDAS.md
  // D-AUDIT-REPORTES.2). Antes de este fix, el `?tipo=Naviera` del link de Sidebar no
  // hacía NADA: ResourcePage nunca leía la query string, así que el listado mostraba
  // TODOS los proveedores sin filtrar y "+ Nuevo" no preseleccionaba el tipo -- bug
  // real encontrado en la auditoría 2026-09-28.
  const [searchParams] = useSearchParams();
  const tipoQuery = searchParams.get('tipo');
  const filtroQuery = tipoQuery ? { tipo: tipoQuery } : {};

  const puedeVer = hasPermiso(`${cfg.recurso}:ver`);
  const puedeCrear = !!cfg.fields && hasPermiso(`${cfg.recurso}:crear`);
  // `cfg.sinEditar` / `cfg.sinBaja`: V1 oculta ese botón en ciertos listados (ej. Navieras
  // tiene el botón Editar comentado; Conceptos de Gastos no pinta <Baja/>).
  const puedeEditar = !!cfg.fields && !cfg.sinEditar && hasPermiso(`${cfg.recurso}:editar`);
  const puedeEliminar = !cfg.sinBaja && hasPermiso(`${cfg.recurso}:eliminar`);

  // `listar` DEBE estar memoizado: `useListado` recrea `cargar` (y por lo tanto
  // vuelve a disparar el `useEffect` de carga) cada vez que `listar` cambia de
  // identidad. Antes de este fix, `listar` era una función nueva en CADA render ->
  // loop infinito de requests (cada respuesta dispara un render -> nuevo `listar` ->
  // nuevo `cargar` -> el effect vuelve a correr) en TODAS las pantallas basadas en
  // ResourcePage. Bug real encontrado probando en el navegador 2026-09-28: tumbó el
  // rate limiter global del backend y bloqueó hasta el login. `cfg` es una referencia
  // estable (viene de `config/resources*.js`, definida a nivel de módulo), así que
  // `cfg.route`/`cfg.extraParams` son deps seguras.
  const listar = useCallback(
    // `cfg.mostrarInactivos`: V1 lista TODOS los registros (activos e inactivos, con botón
    // de Alta para los inactivos); el backend por defecto solo regresa activos.
    (params, opts) => apiClient.get(`/${cfg.route}`, { params: { ...(cfg.mostrarInactivos && !params.is_active ? { is_active: 'all' } : {}), ...params, ...cfg.extraParams, ...filtroQuery, ...(cfg.ocultarIds?.length ? { excluirIds: cfg.ocultarIds.join(',') } : {}) }, signal: opts?.signal }).then((r) => r.data),
    [cfg.route, cfg.extraParams, tipoQuery] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const { filas, paginacion, cargando, error, pagina, setPagina, sortBy, sortOrder, ordenarPor, busqueda, setBusqueda, filtros, cambiarFiltros, recargar } =
    useListado({ listar, ordenPor: cfg.sortBy || 'createdAt', ...(cfg.sortOrder ? { orden: cfg.sortOrder } : {}) });

  // Opciones de catálogo para los `filtrosExtra` de tipo 'ref' (ej. el <select> de
  // Cliente) -- mismo patrón de carga que `FormModal` (ver comentario de `filtrosExtra`
  // arriba). Se cargan una sola vez por recurso (no dependen de `filtros`/búsqueda).
  const [opcionesFiltro, setOpcionesFiltro] = useState({});
  useEffect(() => {
    (cfg.filtrosExtra || []).filter((f) => f.type === 'ref' && f.refRoute).forEach((f) => {
      apiClient.get(`/${f.refRoute}`, { params: { limit: 500, sortBy: f.refLabel || 'nombre_comercial', sortOrder: 'asc' } })
        .then(({ data }) => setOpcionesFiltro((prev) => ({ ...prev, [f.key]: data.data || [] })))
        .catch(() => setOpcionesFiltro((prev) => ({ ...prev, [f.key]: [] })));
    });
  }, [cfg.filtrosExtra]); // eslint-disable-line react-hooks/exhaustive-deps

  async function guardar(valores) {
    const payload = cfg.transformBeforeSave ? cfg.transformBeforeSave(valores, { editando }) : valores;
    if (editando) await apiClient.patch(`/${cfg.route}/${editando._id}${cfg.editSuffix || ''}`, payload);
    else await apiClient.post(`/${cfg.route}`, payload);
    await recargar();
    // Mismo mensaje de éxito que V1 ("Good job!" / "Creado|Actualizado con exito").
    Swal.fire({ title: 'Good job!', text: editando ? 'Actualizado con exito' : 'Creado con exito', icon: 'success', timer: 1500, showConfirmButton: false });
  }

  // Misma confirmación (Swal) y mensajes que `Baja.jsx` de V1.
  async function alternarActivo(row) {
    const baja = row.is_active !== 'No';
    const r = await Swal.fire({
      title: 'Estas seguro?',
      text: baja ? 'Se dara de baja!' : 'Se dara de Alta!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: baja ? 'Si, dar de Baja!' : 'Si, dar de Alta!',
    });
    if (!r.isConfirmed) return;
    try {
      if (baja) await apiClient.delete(`/${cfg.route}/${row._id}`);
      else await apiClient.patch(`/${cfg.route}/${row._id}`, { is_active: 'Si' });
      await recargar();
      Swal.fire({ position: 'center', icon: 'success', title: baja ? 'Se dio de Baja' : 'Se dio de Alta', showConfirmButton: false, timer: 2000 });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: e.message });
    }
  }

  const columnas = [
    ...cfg.columns,
    ...(cfg.fields || cfg.extraRowActions
      ? [{
          key: '__acciones', label: 'Acciones', sortable: false, className: 'text-center',
          render: (row) => (
            <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
              {cfg.fields && puedeEditar && (!cfg.puedeEditarFila || cfg.puedeEditarFila(row)) && (
                <button
                  title="Editar" aria-label="Editar"
                  className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50"
                  onClick={() => { setEditando(row); setFormOpen(true); }}
                >
                  <Pencil size={15} />
                </button>
              )}
              {cfg.fields && puedeEliminar && (
                <button
                  title={row.is_active === 'No' ? 'Dar de alta' : 'Dar de baja'}
                  aria-label={row.is_active === 'No' ? 'Dar de alta' : 'Dar de baja'}
                  className={`p-1.5 rounded-md hover:bg-slate-100 ${row.is_active === 'No' ? 'text-primary-600' : 'text-danger-500'}`}
                  onClick={() => alternarActivo(row)}
                >
                  {row.is_active === 'No' ? <CheckCircle size={15} /> : <XCircle size={15} />}
                </button>
              )}
              {cfg.extraRowActions && cfg.extraRowActions(row, { recargar, hasPermiso })}
            </div>
          ),
        }]
      : []),
  ];

  // Trae TODO el listado filtrado (mismos search/sort/filtros que la pantalla, pero
  // sin paginar -- V1 exportaba siempre el listado completo, nunca solo una página).
  async function traerTodoFiltrado() {
    const { data } = await apiClient.get(`/${cfg.route}`, {
      params: { ...(cfg.mostrarInactivos && !filtros.is_active ? { is_active: 'all' } : {}), ...filtros, ...cfg.extraParams, ...filtroQuery, ...(cfg.ocultarIds?.length ? { excluirIds: cfg.ocultarIds.join(',') } : {}), search: busqueda || undefined, sortBy, sortOrder, page: 1, limit: 100000 },
    });
    return data?.data ?? [];
  }

  async function exportarPDF() {
    setExportando('pdf');
    try {
      const todas = await traerTodoFiltrado();
      await exportTablePdf(cfg.tituloExport || cfg.title, cfg.exportColumns || cfg.columns, todas, cfg.exportFileName ? { fileName: cfg.exportFileName } : undefined);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message });
    } finally {
      setExportando(null);
    }
  }

  async function enviarMail(email) {
    const todas = await traerTodoFiltrado();
    const doc = await buildTablePdf(cfg.tituloExport || cfg.title, cfg.exportColumns || cfg.columns, todas);
    await enviarCorreoAdjunto({
      subject: cfg.emailExport.subject, email, fileName: cfg.emailExport.fileName, docPdf: doc,
      textoHtml: plantillaCorreoV1(cfg.emailExport.cuerpo),
    });
  }

  async function exportarExcel() {
    setExportando('excel');
    try {
      const todas = await traerTodoFiltrado();
      exportTableExcel(cfg.tituloExport || cfg.title, cfg.exportColumns || cfg.columns, todas, { sheetName: cfg.tituloExport || cfg.title, ...(cfg.exportFileName ? { fileName: cfg.exportFileName } : {}) });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message });
    } finally {
      setExportando(null);
    }
  }

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">{cfg.tituloPagina || cfg.title}</h1>
        {puedeCrear && (
          <button onClick={() => { setEditando(null); setFormOpen(true); }} className="btn-primary">
            + Nuevo
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input
          value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar..."
          className="input-base w-64"
        />
        {cfg.exportable && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={exportarPDF} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">
              {exportando === 'pdf' ? 'Generando...' : 'PDF'}
            </button>
            {cfg.emailExport && (
              <button type="button" onClick={() => setMailOpen(true)} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">
                eMail
              </button>
            )}
            <button type="button" onClick={exportarExcel} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">
              {exportando === 'excel' ? 'Generando...' : 'Excel'}
            </button>
          </div>
        )}
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      {cfg.emailExport && (
        <EmailModal open={mailOpen} onClose={() => setMailOpen(false)} titulo={cfg.emailExport.titulo} onEnviar={enviarMail} />
      )}

      {cfg.filtroActivo && (
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Activo</label>
            <select
              className="input-base"
              value={filtros.is_active || ''}
              onChange={(e) => cambiarFiltros({ is_active: e.target.value || undefined })}
            >
              <option value="">Selecciona</option>
              <option value="Si">Activos</option>
              <option value="No">Inactivos</option>
            </select>
          </div>
        </div>
      )}

      {!!cfg.filtrosExtra?.length && (
        <div className="flex flex-wrap items-end gap-3">
          {cfg.filtrosExtra.map((f) => {
            if (f.type === 'date-range') {
              const keyDesde = `${f.key}Desde`;
              const keyHasta = `${f.key}Hasta`;
              return (
                <React.Fragment key={f.key}>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{f.label} inicio</label>
                    <input
                      type="date" className="input-base"
                      value={filtros[keyDesde] || ''}
                      onChange={(e) => cambiarFiltros({ [keyDesde]: e.target.value || undefined })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{f.label} fin</label>
                    <input
                      type="date" className="input-base"
                      value={filtros[keyHasta] || ''}
                      onChange={(e) => cambiarFiltros({ [keyHasta]: e.target.value || undefined })}
                    />
                  </div>
                </React.Fragment>
              );
            }
            if (f.type === 'ref') {
              return (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{f.label}</label>
                  <select
                    className="input-base"
                    value={filtros[f.key] || ''}
                    onChange={(e) => cambiarFiltros({ [f.key]: e.target.value || undefined })}
                  >
                    <option value="">Todos</option>
                    {(opcionesFiltro[f.key] || []).map((o) => (
                      <option key={o._id} value={o._id}>{o[f.refLabel || 'nombre_comercial'] || o.name}</option>
                    ))}
                  </select>
                </div>
              );
            }
            return null;
          })}
        </div>
      )}

      <DataTable
        columns={columnas} data={filas} loading={cargando}
        sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
      />
      <Pagination pagination={paginacion} page={pagina} onChange={setPagina} />

      {cfg.fields && (
        <FormModal
          open={formOpen} onClose={() => setFormOpen(false)}
          title={editando
            ? (typeof cfg.tituloEdicion === 'function' ? cfg.tituloEdicion(editando) : (cfg.tituloEdicion || `Editar ${cfg.title}`))
            : (cfg.tituloAlta || `Nuevo ${cfg.title}`)}
          fields={editando && cfg.editFields ? cfg.editFields : cfg.fields}
          width={cfg.formWidth} defaults={editando ? null : cfg.defaultsAlta}
          initial={editando || (filtroQuery.tipo ? filtroQuery : null)} onSubmit={guardar}
        />
      )}
    </div>
  );
}
